/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Headline, Title, Dropdown, TextField } from '@cambridgeassessment/cambridge-ui';
import {
  CentreBase,
  Series,
  Consortium,
  ConsortiumComponent,
  BUSINESS_UNITS,
  ConsortiumSeries,
} from '@cambridgeassessment/lib-ee-commons';
import CentresSelection from './CentresSelection';
import ComponentsSelection from './ComponentSelection';
import { getSeriesData } from '../../../store/series/thunks';
import { submitConsortium, fetchConsortium } from '../../../store/consortiaManagement/thunks';
import actions, { setValididation, setFilterValue, resetState } from '../../../store/consortiaManagement/actions';
import { setFetchedComponent } from '../../../store/components/actions';
import { RootState } from '../../../store';
import { ConsortiaActionsContainer, StyledParagraph, StyledHeaderContainer } from './ConsortiaManagement.styles';
import { currentTime } from '../../../util/time';
import { StyledInputContainer } from '../Content.styles';

const alphaNumericRegExp = /^[a-zA-Z0-9 -]*$/;

const ConsortiumForm = (): React.ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    userData: { businessUnit },
    series: { seriesData },
    consortiaManagement: { consortium, validation, isSubmittedSuccessfully },
  } = useSelector((state: RootState) => state);
  const [selectedSeries, setSelectedSeries] = useState<Series | null>(null);
  const [name, setName] = useState<string>('');
  const [originalName, setOriginalName] = useState<string>('');
  const [consortiumCentres, setConsortiumCentres] = useState<CentreBase[]>([]);
  const [consortiumComponents, setConsortiumComponents] = useState<ConsortiumComponent[]>([]);
  const [showConsortiumDetails, setShowConsortiumDetails] = useState<boolean>(false);
  const [stayOnPage, setStayOnPage] = useState<boolean>(true);
  const [description, setDescription] = useState<string>('');
  const [isNameAlphaNumeric, setIsNameAlphaNumeric] = useState<boolean>(true);
  const navigateToConsortiaManagementPage = useCallback((): void => history.push('/manageConsortia'), [history]);
  const navigateToHomePage = useCallback((): void => history.push('/'), [history]);
  const { editConsortiumId, duplicateConsortiumId } = useParams<{
    editConsortiumId: string;
    duplicateConsortiumId: string;
  }>();

  const isDisabled =
    !name ||
    !selectedSeries ||
    !validation?.isNameUnique ||
    !isNameAlphaNumeric ||
    consortiumCentres.length < 2 ||
    consortiumComponents.length < 1 ||
    description?.length > 150;

  useEffect(() => {
    if (businessUnit?.id !== BUSINESS_UNITS.OCR.id) navigateToHomePage();
  }, [businessUnit]);

  useEffect(() => {
    const consortiumId = editConsortiumId || duplicateConsortiumId;
    if (consortiumId) {
      dispatch(fetchConsortium(consortiumId));
      setShowConsortiumDetails(true);
    }
  }, [editConsortiumId, duplicateConsortiumId]);

  useEffect(() => {
    if (businessUnit) dispatch(getSeriesData(businessUnit.id, currentTime().year()));
  }, [businessUnit, dispatch]);

  useEffect(() => {
    if (consortium) {
      setName(consortium.name);
      setOriginalName(consortium.name);
      setDescription(consortium.description);
      if (editConsortiumId) {
        setSelectedSeries({ ...consortium.series, businessUnit: { id: consortium.series.businessUnitId } });
      }
    }
    return () => {
      dispatch(resetState());
      dispatch(setFetchedComponent());
    };
  }, [consortium, editConsortiumId]);

  useEffect(() => {
    if (isSubmittedSuccessfully) {
      dispatch(resetState());
      dispatch(setFetchedComponent());
      resetLocalState();
      if (!stayOnPage || editConsortiumId || duplicateConsortiumId) {
        dispatch(setFilterValue('current'));
        navigateToConsortiaManagementPage();
      }
    }
  }, [isSubmittedSuccessfully, stayOnPage]);

  const onNameChange = (val: string): void => {
    setName(val);
    setIsNameAlphaNumeric(alphaNumericRegExp.test(val));
    dispatch(setValididation({ ...validation, isNameUnique: true }));
  };

  const onCancelClick = (): void => {
    if (duplicateConsortiumId) {
      dispatch(actions.setFilterValue('past'));
    }
    navigateToConsortiaManagementPage();
  };

  const resetLocalState = (): void => {
    setName('');
    setDescription('');
    setSelectedSeries(null);
    setConsortiumCentres([]);
    setConsortiumComponents([]);
    setShowConsortiumDetails(false);
    setStayOnPage(true);
  };

  const createOrUpdateConsortium = (createNew?: boolean): void => {
    setStayOnPage(!!createNew);
    const updatedConsortium: Consortium = {
      name,
      description: description || undefined,
      centres: consortiumCentres,
      components: consortiumComponents,
      series: {
        id: selectedSeries?.id,
        businessUnitId: selectedSeries?.businessUnit.id,
      } as ConsortiumSeries,
    };
    dispatch(submitConsortium({ _id: editConsortiumId, ...updatedConsortium }));
  };

  const onSeriesSelect = (series: Series): void => {
    setSelectedSeries(series);
    dispatch(setValididation({ ...validation, isNameUnique: true }));
  };

  const renderError = (isNameUnique: boolean, isValidName: boolean): string => {
    if (!isNameUnique) return 'Name is not unique for the same series';
    if (!isValidName) return 'Only alphanumeric characters with "-" and space are allowed';
    return '';
  };

  const renderParagraphText = () => {
    if (editConsortiumId)
      return 'Edit consortium by adding the series, a name and description, at least two centres and at least one component.';
    if (duplicateConsortiumId)
      return (
        <>
          Fields below have been pre-populated for you with information from <b>{originalName}</b>. You can assign a new
          series, change the name or description, or add and remove specifications and components.
        </>
      );
    return 'Create consortium by adding the series, a name and description, at least two centres and at least one component.';
  };

  const renderSeries = (item: Series): string => item.name;

  return (
    <div data-qa-id='consortium-form'>
      <StyledHeaderContainer>
        <Headline>{editConsortiumId ? ' Edit consortium' : 'New consortium'}</Headline>
      </StyledHeaderContainer>
      <StyledParagraph>{renderParagraphText()}</StyledParagraph>
      <StyledInputContainer>
        <Title id='series'>Series</Title>
        <StyledInputContainer>
          <Dropdown<Series>
            data={(seriesData as Series[]) || []}
            renderOption={renderSeries}
            renderSelectedOption={renderSeries}
            dataQaId='series-select'
            onChange={onSeriesSelect}
            value={selectedSeries}
            placeholder='Please select'
            title='Select series'
          />
        </StyledInputContainer>
      </StyledInputContainer>
      <StyledInputContainer>
        <Title>Consortium name</Title>
        <TextField
          dataQaId='enter-consortium-name'
          onChange={onNameChange}
          placeholder='Please enter consortium name'
          value={name}
          errorMsg={renderError(validation?.isNameUnique, isNameAlphaNumeric)}
        />
      </StyledInputContainer>
      <StyledInputContainer>
        <Title>Consortium description</Title>
        <TextField
          dataQaId='enter-consortium-description'
          onChange={setDescription}
          placeholder='Please enter consortium description'
          value={description}
          errorMsg={description?.length > 150 ? 'Description cannot exceed 150 characters' : ''}
          multiline={true}
          helperText='Optional'
        />
      </StyledInputContainer>
      {duplicateConsortiumId && (
        <StyledParagraph data-qa-id='duplicate-consortium-paragraph'>
          Centres, specification(s) and component(s) listed below are already included in <br></br>the consortium. Click
          on “X” to remove them.
        </StyledParagraph>
      )}
      {showConsortiumDetails && (
        <>
          <CentresSelection onCentresChange={setConsortiumCentres} centres={consortiumCentres} />
          <ComponentsSelection onComponentsChange={setConsortiumComponents} components={consortiumComponents} />
        </>
      )}
      <ConsortiaActionsContainer>
        {!showConsortiumDetails ? (
          <Button
            onClick={() => setShowConsortiumDetails(true)}
            disabled={!selectedSeries || !name || description?.length > 150}
            color='primary'
            qaId='next-add-details-button'
          >
            Next - add consortium details
          </Button>
        ) : (
          <Button onClick={() => createOrUpdateConsortium(false)} disabled={isDisabled} qaId='submit-consortium-button'>
            {editConsortiumId || duplicateConsortiumId ? 'Save' : 'Save and finish'}
          </Button>
        )}
        {!editConsortiumId && !duplicateConsortiumId && showConsortiumDetails && (
          <Button
            onClick={() => createOrUpdateConsortium(true)}
            variant='outlined'
            disabled={isDisabled}
            qaId='create-another-consortium-button'
          >
            Save and create another
          </Button>
        )}
        <Button onClick={onCancelClick} variant='text' qaId='cancel-consortium-button'>
          Cancel
        </Button>
      </ConsortiaActionsContainer>
    </div>
  );
};

export default ConsortiumForm;
